/**
 * CardLabelButton — print an individual card's `tcvs:card:` QR label.
 *
 * A card carries no physical label by default; it is tracked through its
 * containing Storage_Batch's label. An individual card label is needed only when
 * a card is (or is about to be) separated from its batch — pulled for
 * processing-then-restock, pulled to ship, or found loose for restock — so this
 * button is co-located with those moments (the Pick action, the Find lookup, and
 * the catalog detail page) rather than living in a standalone label destination.
 *
 * The button fetches the label for the card identifier returned by `getCardId`
 * (a literal cardId OR a human short code — resolved server-side to the
 * canonical `tcvs:card:<cardId>` token), renders it inline via {@link LabelTile},
 * and prints. Printing a label is independent of any pick/putaway mutation — it
 * never pulls or moves the card.
 */

import { useCallback, useState } from 'react';
import type { ApiResult } from '../auth/api-client';
import { LabelTile } from './LabelTile';
import { printSingleLabel } from './label-print';
import { useStorageApi, type CardLabelResponse } from './storage-api';

/** Extract a human message from an ApiResult failure (mirrors StoragePage). */
function failMessage(result: { ok: false; error: string; message?: string }): string {
  return result.message ?? result.error;
}

/**
 * Visual weight of the trigger button. `'secondary'` (default) is the quiet,
 * bordered style used where the button sits beside a more important action
 * (e.g. next to **Pick**), so it doesn't compete with that page's primary
 * action. `'primary'` is the filled, colored default-button style, used where
 * printing the label IS the main thing on the surface (e.g. the catalog detail
 * page) so it reads clearly as clickable.
 */
type CardLabelButtonVariant = 'primary' | 'secondary';

export function CardLabelButton({
  getCardId,
  variant = 'secondary',
}: {
  getCardId: () => string;
  variant?: CardLabelButtonVariant;
}): JSX.Element {
  const api = useStorageApi();
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState<CardLabelResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const onPrint = useCallback(async () => {
    const id = getCardId().trim();
    setError(null);
    setLabel(null);
    if (id === '') {
      setError('Enter a card id or short code first.');
      return;
    }
    setLoading(true);
    const res: ApiResult<unknown> = await api.labels('cards', id);
    setLoading(false);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    // The labels route returns a CardLabelResponse for the `cards` type.
    setLabel(res.data as CardLabelResponse);
  }, [api, getCardId]);

  return (
    <div>
      <button
        type="button"
        // `primary` uses the filled default-button style (no class needed);
        // `secondary` is the quiet bordered variant so it doesn't compete with a
        // neighbouring primary action like Pick.
        className={variant === 'secondary' ? 'tcvs-btn-secondary' : undefined}
        onClick={() => void onPrint()}
        disabled={loading}
      >
        {loading ? 'Fetching label…' : 'Print card label'}
      </button>
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
      {label !== null ? (
        <LabelTile
          token={label.token}
          entityId={label.cardId}
          {...(label.shortCode !== undefined ? { shortCode: label.shortCode } : {})}
          qrMaxWidth={260}
          containerStyle={{
            border: '2px dashed #333',
            padding: 16,
            maxWidth: 360,
            marginTop: 12,
            textAlign: 'center',
          }}
        >
          <button type="button" onClick={printSingleLabel}>
            Print
          </button>
        </LabelTile>
      ) : null}
    </div>
  );
}
